/**
 * LoginForm — the standard username/password login form
 */

import React from "react";
import type { BackendConfig } from "../../types/config";

interface LoginFormProps {
  username: string;
  setUsername: (v: string) => void;
  password: string;
  setPassword: (v: string) => void;
  isLoggingIn: boolean;
  error: string;
  config: BackendConfig;
  onSubmit: (e: React.FormEvent) => void;
  onForgotPassword: () => void;
  onSignup: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({
  username,
  setUsername,
  password,
  setPassword,
  isLoggingIn,
  error,
  config,
  onSubmit,
  onForgotPassword,
  onSignup,
}) => (
  <>
    <h1>Welcome</h1>
    <p>{config.messages.loginSubtext}</p>
    {/*
      AL-470: the set-password step on first sign-in used to arrive unannounced,
      and the screen that delivered it claimed the temporary password had expired.
      That copy is fixed in NewPasswordForm; this removes the surprise a step
      earlier, which is where the OPS enrolment page handles it.

      Rendered only when configured, so a deployment that has no use for it can
      drop the string rather than needing a code change.
    */}
    {config.messages.loginTempPasswordHint && (
      <p className="login-hint" data-testid="login-temp-password-hint">
        {config.messages.loginTempPasswordHint}
      </p>
    )}
    {error && (
      <div className="error-message" data-testid="login-error">
        {error}
      </div>
    )}
    <form onSubmit={onSubmit} className="login-form" data-testid="login-form">
      <div className="form-group">
        <label>Username</label>
        <input
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder="Enter your username"
          disabled={isLoggingIn}
          required
          data-testid="login-username-input"
        />
      </div>
      <div className="form-group">
        <label>Password</label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Enter your password"
          disabled={isLoggingIn}
          required
          data-testid="login-password-input"
        />
      </div>
      <button
        type="submit"
        className="login-btn"
        disabled={isLoggingIn}
        data-testid="login-submit-btn"
      >
        {isLoggingIn ? "Signing in..." : "Sign In"}
      </button>
    </form>
    <div style={{ marginTop: "16px", textAlign: "center" }}>
      <p
        style={{
          margin: "0 0 12px 0",
          fontSize: "14px",
          color: "#666",
        }}
      >
        New to our platform?
      </p>
      <button
        type="button"
        className="signup-btn"
        data-testid="signup-btn"
        onClick={onSignup}
        style={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          border: "none",
          padding: "10px 24px",
          borderRadius: "6px",
          fontSize: "14px",
          fontWeight: 600,
          cursor: "pointer",
          width: "100%",
          transition: "all 0.3s ease",
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.transform =
            "translateY(-2px)";
          (e.currentTarget as HTMLButtonElement).style.boxShadow =
            "0 6px 20px rgba(102, 126, 234, 0.4)";
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.transform =
            "translateY(0)";
          (e.currentTarget as HTMLButtonElement).style.boxShadow =
            "0 4px 12px rgba(102, 126, 234, 0.3)";
        }}
      >
        Activate Your Regulatory Capability
      </button>
    </div>
    <button
      type="button"
      className="forgot-password-btn"
      onClick={onForgotPassword}
      data-testid="forgot-password-btn"
    >
      Forgot Password?
    </button>
  </>
);

export default LoginForm;
